import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getValidAuthToken, decodeToken } from '@/utils/auth';

type Props = {
  requiredScopes: string[];
  allowMaster?: boolean;
  children: ReactNode;
  behavior?: 'hide' | 'disable' | 'redirect';
  showToast?: boolean;
  toastMessage?: string;
};

export const RestrictedComponent = ({
  requiredScopes,
  allowMaster = false,
  children,
  behavior = 'hide',
  showToast = false,
  toastMessage = 'Você não tem permissão para acessar este recurso.',
}: Props) => {
  const navigate = useNavigate();

  // Get valid (non-expired) token
  const token = getValidAuthToken();

  const handleToast = () => {
    if (showToast) {
      toast.error(toastMessage, { id: 'no-permission' });
    }
  };

  useEffect(() => {
    if (!token && behavior === 'redirect') {
      navigate('/login', { state: { toastUnauthorized: true } });
    }
  }, [token, behavior, navigate]);

  if (!token) {
    handleToast();
    return behavior === 'hide' ? null : <DisabledWrapper>{children}</DisabledWrapper>;
  }

  const decoded = decodeToken(token);

  if (!decoded) {
    if (behavior === 'redirect') {
      navigate('/login', { state: { toastUnauthorized: true } });
      return null;
    }
    handleToast();
    return behavior === 'hide' ? null : <DisabledWrapper>{children}</DisabledWrapper>;
  }

  const userScopes = decoded.scope ?? [];
  const hasAllScopes =
    (allowMaster && decoded.isMaster === true) || requiredScopes.every((scope) => userScopes.includes(scope));

  if (!hasAllScopes) {
    if (behavior === 'redirect') {
      navigate('/login', { state: { toastUnauthorized: true } });
      return null;
    }

    if (behavior === 'disable') {
      handleToast();
      return <DisabledWrapper>{children}</DisabledWrapper>;
    }

    return null;
  }

  return <>{children}</>;
};

const DisabledWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <div
      style={{
        opacity: 0.5,
        pointerEvents: 'none',
        cursor: 'not-allowed',
      }}
    >
      {children}
    </div>
  );
};
